import * as React from 'react';
import { useRef } from 'react';

import { Complete, Info } from '@digital-hig/icon/display-icons';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  safePolygon,
  arrow,
  FloatingArrow,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';

export interface TooltipProps {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: 'click' | 'hover' | 'focus';
}

type ContextType = ReturnType<typeof useTooltip> | null;

export function useTooltip({
  initialOpen = false,
  placement = 'bottom-end',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  trigger,
}: TooltipProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const arrowRef = useRef(null);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), shift(), flip(), arrow({ element: arrowRef })],
  });
  const { context } = data;

  const interactions = useInteractions([
    useHover(context, {
      mouseOnly: true,
      move: false,
      enabled: controlledOpen == null && !trigger?.includes('click'),
      handleClose: safePolygon(),
    }),
    useFocus(context, {
      enabled: controlledOpen == null && !trigger?.includes('click'),
    }),
    useClick(context, {
      enabled:
        controlledOpen == null &&
        typeof trigger === 'string' &&
        trigger.includes('click'),
    }),
    useDismiss(context),
    useRole(context, { role: 'tooltip' }),
  ]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      arrowRef,
    }),
    [open, setOpen, interactions, data, arrowRef]
  );
}

const TooltipContext = React.createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({
  children,
  ...options
}: { children: React.ReactNode } & TooltipProps) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

export const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setReference, propRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      })
    );
  }

  return (
    <button
      type="button"
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

type TooltipContentProps = React.HTMLProps<HTMLDivElement> & {
  children: React.ReactNode;
  // When style is defined, eslint complains that 'style' PropType is defined but prop is never used - react/no-unused-prop-types
  // When it's missing, eslint complains that 'style' is missing in props validation - react/prop-types
  // eslint-disable-next-line react/no-unused-prop-types
  style?: React.CSSProperties;
};

export const TooltipContent = React.forwardRef<
  HTMLDivElement,
  TooltipContentProps
>(function TooltipContent(props: TooltipContentProps, propRef) {
  const { arrowRef, ...context } = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  return (
    <FloatingPortal>
      {context.open && (
        <div
          className="au-drop-shadow-user-tooltip au-z-[1400]"
          ref={ref}
          style={{
            position: context.strategy,
            top: context.y ?? 0,
            left: context.x ?? 0,
            visibility: context.x == null ? 'hidden' : 'visible',
            ...props.style,
          }}
          {...context.getFloatingProps()}
        >
          {props.children}
          <FloatingArrow ref={arrowRef} context={context.context} fill="#fff" />
        </div>
      )}
    </FloatingPortal>
  );
});

export function TooltipInfoContent({ children }: TooltipContentProps) {
  const context = useTooltipContext();
  return (
    <div className="au-min-h-c85 au-border-l-c3 au-border-l-iris au-rounded-c5 au-p-c15 au-flex au-bg-white au-relative au-w-c260">
      <Info width={21} height={21} className="au-min-w-c21" />
      <div className="au-ml-c10 au-pr-c30 au-text-c14 au-leading-c18 au-text-gray-3c">
        {children}
      </div>

      <button
        type="button"
        className="au-absolute au-top-c15 au-right-c15 au-cursor-pointer"
        aria-label="close button"
        onClick={() => {
          context.setOpen(false);
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20px"
          height="20px"
          viewBox="0 0 21 21"
        >
          <path
            stroke="#000"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="m15.979 4.67-5.834 5.834m0 0-5.833 5.834m5.833-5.834L4.312 4.671m5.833 5.833 5.834 5.834"
          />
        </svg>
      </button>
    </div>
  );
}

export function TooltipSuccessContent({ children }: TooltipContentProps) {
  const context = useTooltipContext();
  return (
    <div className="au-min-h-c85 au-rounded-c5 au-p-c15 au-flex au-bg-white au-relative au-w-c260">
      <Complete width={24} height={24} className="au-min-w-c24" />
      <div className="au-ml-c10 au-pr-c30 au-text-c14 au-leading-c18 au-text-gray-3c">
        {children}
      </div>

      <button
        type="button"
        className="au-absolute au-top-c15 au-right-c15 au-cursor-pointer"
        aria-label="close button"
        onClick={() => {
          context.setOpen(false);
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20px"
          height="20px"
          viewBox="0 0 21 21"
          className="au-min-w-c20"
        >
          <path
            stroke="#000"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="m15.979 4.67-5.834 5.834m0 0-5.833 5.834m5.833-5.834L4.312 4.671m5.833 5.833 5.834 5.834"
          />
        </svg>
      </button>
    </div>
  );
}
